import { useState, useEffect, useCallback } from "react";
import { useWebSocketContext } from "../contexts/WebSocketContext";
import { useToast } from "../components/ui/Toast/ToastProvider";
import { InviteService } from "../services/inviteService";
import type { PendingInvitation } from "../types/invite";

export function usePendingInvitations() {
  const { lastMessage } = useWebSocketContext();
  const { showToast } = useToast();
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await InviteService.listMyInvitations();
      setInvitations(data ?? []);
    } catch {
      // keep previous list on transient failure
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load (REST is the source of truth).
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live nicety: toast + refresh when a campaign_invite arrives.
  useEffect(() => {
    if (!lastMessage || lastMessage.type !== "campaign_invite") return;
    const data = (lastMessage.data ?? {}) as { campaignName?: string; invitedBy?: string };
    showToast({
      type: "info",
      message: `${data.invitedBy ?? "Someone"} invited you to ${data.campaignName ?? "a chronicle"}`,
      duration: 8000,
    });
    refresh();
  }, [lastMessage, showToast, refresh]);

  const accept = useCallback(async (id: number): Promise<number> => {
    const res = await InviteService.acceptInvitation(id);
    setInvitations((prev) => prev.filter((i) => i.id !== id));
    return res.campaignId;
  }, []);

  const reject = useCallback(async (id: number): Promise<void> => {
    await InviteService.rejectInvitation(id);
    setInvitations((prev) => prev.filter((i) => i.id !== id));
  }, []);

  return { invitations, count: invitations.length, loading, accept, reject, refresh };
}
